import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, Minus, X, BookmarkPlus } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useFmt } from "../context/CurrencyContext";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { resolveImageUrl } from "../components/ImageUpload";

export default function BuildABox() {
  const [flavours, setFlavours] = useState([]);
  const [config, setConfig] = useState({ price_per_pop: 650, min_pops: 4, max_pops: 24, default_pops: 8 });
  const [selection, setSelection] = useState([]); // list of flavour names (with duplicates)
  const { addItem } = useCart();
  const { user } = useAuth();
  const fmtJMD = useFmt();

  useEffect(() => {
    api.get("/products?build_a_box=true").then((r) => setFlavours(r.data)).catch(() => {});
    api.get("/build-a-box/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const total = useMemo(() => selection.length * config.price_per_pop, [selection, config]);

  const add = (name) => {
    if (selection.length >= config.max_pops) {
      toast.error(`Max ${config.max_pops} pops per box`);
      return;
    }
    setSelection((s) => [...s, name]);
  };
  const removeOne = (name) => {
    setSelection((s) => {
      const idx = s.lastIndexOf(name);
      if (idx < 0) return s;
      const c = [...s];
      c.splice(idx, 1);
      return c;
    });
  };

  const addToCart = () => {
    if (selection.length < config.min_pops) {
      toast.error(`Minimum ${config.min_pops} pops required`);
      return;
    }
    addItem({
      type: "custom_box",
      ref_id: null,
      name: `Custom Box · ${selection.length} pops`,
      price: total,
      quantity: 1,
      custom_flavours: selection,
    });
    toast.success(`Custom box (${selection.length} pops) added to cart`);
    setSelection([]);
  };

  const saveBox = async () => {
    if (!user || user.role !== "customer") {
      toast.error("Sign in as a customer to save boxes");
      return;
    }
    if (selection.length < config.min_pops) {
      toast.error(`Add at least ${config.min_pops} pops first`);
      return;
    }
    const name = window.prompt("Name this box (e.g. 'Family Friday')");
    if (!name) return;
    try {
      await api.post("/account/boxes", { name, flavour_names: selection });
      toast.success(`Saved “${name}” to your account`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  // Count per flavour
  const counts = selection.reduce((a, n) => ((a[n] = (a[n] || 0) + 1), a), {});

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12 py-12 sm:py-16" data-testid="build-a-box-page">
      <div className="mb-10 sm:mb-12">
        <div className="overline text-zinc-500 mb-3">Build Yuh Own Box · JMD ${config.price_per_pop} / pop</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">Mix your own</h1>
        <p className="mt-3 sm:mt-4 text-zinc-600 text-base sm:text-lg max-w-2xl">
          Pick any combination — minimum {config.min_pops}, maximum {config.max_pops}. Duplicates welcome.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
        {/* Flavour palette */}
        <div className="lg:col-span-8">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-5">
            {flavours.map((f) => {
              const cnt = counts[f.name] || 0;
              const disabled = f.in_stock === false || f.stock === 0;
              return (
                <div key={f.id} className="rounded-2xl overflow-hidden bg-white border border-zinc-100 flex flex-col" data-testid={`bab-flavour-${f.id}`}>
                  <div className="aspect-square overflow-hidden">
                    <img src={resolveImageUrl(f.image_url)} alt={f.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-3 sm:p-4 flex-1 flex flex-col">
                    <div className="font-semibold text-sm break-words">{f.name}</div>
                    <div className="text-xs text-zinc-500">{fmtJMD(f.price)}</div>
                    <div className="mt-3 flex items-center justify-between gap-2 mt-auto flex-wrap">
                      <div className="flex items-center gap-1 sm:gap-2 border border-zinc-200 rounded-full px-1">
                        <button onClick={() => removeOne(f.name)} disabled={cnt === 0} className="p-1.5 disabled:opacity-30" data-testid={`bab-remove-${f.id}`}><Minus size={12} /></button>
                        <span className="w-5 text-center text-xs font-semibold">{cnt}</span>
                        <button onClick={() => add(f.name)} disabled={disabled} className="p-1.5 disabled:opacity-30" data-testid={`bab-add-${f.id}`}><Plus size={12} /></button>
                      </div>
                      {disabled && <span className="text-[10px] text-red-500 font-semibold">Sold out</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Box summary */}
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-24 rounded-2xl border border-zinc-200 bg-white p-5 sm:p-6" data-testid="bab-summary">
            <div className="overline text-zinc-500 mb-3">Your box</div>
            <div className="flex items-baseline gap-2">
              <span className="font-display text-4xl font-bold">{selection.length}</span>
              <span className="text-zinc-500">/ {config.max_pops} pops</span>
            </div>
            <div className="w-full h-2 bg-zinc-100 rounded-full overflow-hidden mt-3">
              <div className="h-full bg-[#F74B64] transition-all" style={{ width: `${(selection.length / config.max_pops) * 100}%` }} />
            </div>

            <div className="mt-6 space-y-2 max-h-64 overflow-y-auto pr-2">
              <AnimatePresence>
                {Object.entries(counts).map(([name, c]) => (
                  <motion.div key={name}
                              initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}
                              className="flex items-center justify-between text-sm p-2 rounded-lg bg-zinc-50">
                    <span>{c} × {name}</span>
                    <button onClick={() => removeOne(name)} className="text-zinc-400 hover:text-red-500" data-testid={`bab-sum-remove-${name}`}>
                      <X size={14} />
                    </button>
                  </motion.div>
                ))}
              </AnimatePresence>
              {selection.length === 0 && <div className="text-sm text-zinc-400">No pops selected yet.</div>}
            </div>

            <div className="mt-6 pt-6 border-t border-zinc-200 flex items-center justify-between">
              <span className="text-sm text-zinc-500">Total</span>
              <span className="font-display text-2xl font-bold">{fmtJMD(total)}</span>
            </div>
            {selection.length < config.min_pops ? (
              <button
                onClick={() => toast.error(`Add ${config.min_pops - selection.length} more pop${config.min_pops - selection.length === 1 ? "" : "s"} to continue`)}
                className="w-full mt-5 rounded-full px-6 py-3 font-semibold border-2 border-dashed border-[#F74B64] text-[#F74B64] hover:bg-[#FFF5F6] transition"
                data-testid="bab-add-to-cart">
                Add {config.min_pops - selection.length} more pop{config.min_pops - selection.length === 1 ? "" : "s"} to continue
              </button>
            ) : (
              <button onClick={addToCart}
                      className="btn-primary w-full mt-5" data-testid="bab-add-to-cart">
                Add custom box to cart · {fmtJMD(total)}
              </button>
            )}
            {user && user.role === "customer" && selection.length >= config.min_pops && (
              <button onClick={saveBox}
                      className="w-full mt-2 inline-flex items-center justify-center gap-2 text-sm font-semibold px-4 py-2 rounded-full border border-zinc-200 hover:bg-zinc-50"
                      data-testid="bab-save-box">
                <BookmarkPlus size={14} /> Save to my boxes for 1-click reorder
              </button>
            )}
            <div className="text-xs text-zinc-500 mt-2 text-center">
              Minimum {config.min_pops} pops · Maximum {config.max_pops} pops
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
